import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ProviderProfileActionEnum, ServicingAccessPoint, AccessPointFilterState, AccessPointSortOptionEnum, emptyAccessPointFilterState } from "@app/projects/network-assessment/provider-hub/interfaces";
import { toast } from "react-toastify";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { AccessPointDetails } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/accessPointDetails";
import { useLocation } from "react-router-dom";
import { ProviderProfileRouteState } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileRouteState";
import { ProviderProfileReferrerEnum } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileReferrerEnum";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ProviderActionStatusEnum, NPIStatus, EntityTypeEnum, LocationConfidenceScoresLabelEnum } from "@app/projects/enumerators";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { NewActionMenu } from "@app/shared/action-menu";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { showLegacyQuinnButtons } from "@app/projects/network-assessment/provider-hub/ai-assistant/aiChatPanelAuthorizationHelper";
import { NetworkIntegrityNotificationFlag } from "@app/projects/network-assessment/provider-hub/network-integrity-components/NetworkIntegrityNotificationFlag";
import { ProviderNetworkTypeEnum, providerNetworkTypeDisplayOrder } from "@app/shared/enums/providerNetworkTypeEnum";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { AccessPointCard } from "@app/projects/network-assessment/provider-hub/provider-profile/AccessPointCard";
import { AccessPointCardsPaging } from "@app/projects/network-assessment/provider-hub/provider-profile/AccessPointCardsPaging";
import { AccessPointDetailsSection } from "@app/projects/network-assessment/provider-hub/provider-profile/AccessPointDetails";
import { ProviderHubPCIAccordion } from "@app/projects/network-assessment/provider-hub/provider-profile/ProviderHubPCIAccordion";
import ProviderHubProfileActionFlyout from "@app/projects/network-assessment/provider-hub/provider-profile/ProviderHubProfileActionFlyout";
import ProviderProfileCriticality from "@app/projects/network-assessment/provider-hub/provider-profile/ProviderProfileCriticality";
import { ProviderProfileKeyPoints } from "@app/projects/network-assessment/provider-hub/provider-profile/ProviderProfileKeyPoints";
import { ProviderProfileNetworkIntegrity } from "@app/projects/network-assessment/provider-hub/provider-profile/ProviderProfileNetworkIntegrity";
import { ProviderProfileOverview } from "@app/projects/network-assessment/provider-hub/provider-profile/ProviderProfileOverview";
import ProviderProfileTopMarketAlternatives from "@app/projects/network-assessment/provider-hub/provider-profile/ProviderProfileTopMarketAlternatives";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import ProviderAffiliationService from "@app/projects/providerAffiliationsService";
import { ContractEntityAffiliationModel } from "@app/projects/servicing-provider/providerAffiliation";
import { ProviderProfileAffiliationAccordion } from "./ProviderProfileAffiliationAccordion";
import { AccessPointFilterModal } from "@app/projects/network-assessment/provider-hub/provider-profile/AccessPointFilterModal";
import { QesActiveFiltersBar } from "@app/shared/QesActiveFiltersBar";
import type { FilterChipDescriptor } from "@app/shared/QesActiveFiltersBar";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { accessPointSortOptionLabel, providerNetworkTypeDisplayName } from "@app/projects/network-assessment/provider-hub/utility";
import { locationConfidenceScoreToDisplay } from "@app/projects/utility";

// Sort weights for LocationConfidenceHighToLow — higher value = listed first
const CONFIDENCE_SORT_ORDER: Partial<Record<LocationConfidenceScoresLabelEnum, number>> = {
  [LocationConfidenceScoresLabelEnum.High]: 4,
  [LocationConfidenceScoresLabelEnum.Low]: 2,
  [LocationConfidenceScoresLabelEnum.Medium]: 3,
  [LocationConfidenceScoresLabelEnum.VeryLow]: 1,
};

// Each object's keys are alphabetical (key < label < statuses) — satisfies sort-keys.
const ACTION_CHIP_CONFIGS: ReadonlyArray<{ key: string; label: string; statuses: ProviderActionStatusEnum[] }> = [
  { key: "action-excluded", label: "Excluded", statuses: [ProviderActionStatusEnum.FullyExcluded] },
  { key: "action-targeted", label: "Targeted", statuses: [ProviderActionStatusEnum.FullyTargeted] },
  { key: "action-terminated", label: "Terminated", statuses: [ProviderActionStatusEnum.FullyTerminated] },
];

// Middle breadcrumb label for the profile the user drilled from. Anything not listed here (ProviderHubLandingPage /
// ProviderProfile) and deep links fall back to "Provider Hub". The crumb's click is always a browser Back.
const REFERRER_CRUMB_TEXT: Partial<Record<ProviderProfileReferrerEnum, string>> = {
  [ProviderProfileReferrerEnum.ContractEntityProfile]: "Contract Entity",
  [ProviderProfileReferrerEnum.HospitalProfile]: "Hospital",
  [ProviderProfileReferrerEnum.OrganizationProfile]: "Organization",
};

export const ProviderHubProviderProfile: FC = () => {
  const location = useLocation<ProviderProfileRouteState>();
  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);
  const userContext = useContext(UserContext);

  const featureFlagContext = useContext(FeatureFlagContext);

  const providerHubService = useRef(new ProviderHubService()).current;
  const providerAffiliationService = useRef(new ProviderAffiliationService()).current;

  const { selectedProvider, adequacyCriticalityLoading, adequacyProviderScore } = providerHubContext;
  const [accessPoints, setAccessPoints] = useState<ServicingAccessPoint[]>([]);
  const [selectedAccessPoint, setSelectedAccessPoint] = useState<ServicingAccessPoint>();
  const [isLoadingAccessPoints, setIsLoadingAccessPoints] = useState(true);
  const [affiliations, setAffiliations] = useState<ContractEntityAffiliationModel[]>([]);
  const [isLoadingAffiliations, setIsLoadingAffiliations] = useState(true);
  // Contract entities known to have NO Provider Hub profile (no provider with a primary affiliation). Their
  // names render as plain text instead of hyperlinks so users don't hit a page that spins forever. Inverted
  // (without-profile) so links stay active while loading and if the check fails - navigating to a contract
  // entity only needs the id already in hand, so failing open costs nothing.
  const [contractEntityIdsWithoutProfile, setContractEntityIdsWithoutProfile] = useState<Set<number>>(new Set());
  // Organizations are tracked positively instead, because navigating to one needs the contract entity its
  // profile actually resolves under (the org's own professional CE, which may differ from the CE it is listed
  // beneath in the accordion). Only NPIs in this map can be linked; the flag distinguishes "the lookup came
  // back and this org has no profile" from "the lookup has not resolved", which must not claim either.
  const [organizationNavContractEntityIdByNpi, setOrganizationNavContractEntityIdByNpi] = useState<Map<string, number>>(new Map());
  const [isOrganizationAvailabilityResolved, setIsOrganizationAvailabilityResolved] = useState(false);
  const [entityType, setEntityType] = useState<EntityTypeEnum>(EntityTypeEnum.Unknown);
  const [isLoadingEntityType, setIsLoadingEntityType] = useState(true);
  const [accessPointDetailsMap, setAccessPointDetailsMap] = useState<Map<number, AccessPointDetails>>(new Map<number, AccessPointDetails>());
  const [isLoadingApDetails, setIsLoadingApDetails] = useState(true);
  const filtersButtonRef = useRef<HTMLButtonElement>(null);
  const [filterState, setFilterState] = useState<AccessPointFilterState>(emptyAccessPointFilterState);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [sortOption, setSortOption] = useState<AccessPointSortOptionEnum>(AccessPointSortOptionEnum.Default);

  const outOfNetworkTypes = useRef<ProviderNetworkTypeEnum[]>([ProviderNetworkTypeEnum.Prospect, ProviderNetworkTypeEnum.Market, ProviderNetworkTypeEnum.NetNew]).current;

  const accessPointsForProjection = useMemo(() => {
    return accessPoints.filter(ap => outOfNetworkTypes.includes(ap.providerNetworkType)
      && !([ProviderActionStatusEnum.FullyTargeted, ProviderActionStatusEnum.FullyExcluded].includes(ap.providerActionStatus)));
  }, [accessPoints, outOfNetworkTypes]);

  // First non net-new impact access point + all net-new appended
  // Used in the ProjectionProviderDetails flyout to prevent problems with specialty group codes and provider IDs
  const effectiveAccessPointsForProjection = useMemo(() => {
    return [
      accessPointsForProjection.find(a => a.providerNetworkType !== ProviderNetworkTypeEnum.NetNew),
      ...accessPointsForProjection.filter(a => a.providerNetworkType === ProviderNetworkTypeEnum.NetNew)
    ].filter(a => !!a);
  }, [accessPointsForProjection]);

  const accessPointsForTermination = useMemo(() => {
    return accessPoints
      .filter(ap => [ProviderNetworkTypeEnum.Network].includes(ap.providerNetworkType) && !([ProviderActionStatusEnum.FullyTerminated].includes(ap.providerActionStatus)));
  }, [accessPoints]);

  const networkAccessPoints = useMemo(() => {
    return accessPoints.filter(ap => [ProviderNetworkTypeEnum.Network].includes(ap.providerNetworkType));
  }, [accessPoints]);

  const outOfNetworkAccessPoints = useMemo(() => {
    return accessPoints.filter(ap => outOfNetworkTypes.includes(ap.providerNetworkType));
  }, [accessPoints, outOfNetworkTypes]);

  const hasNonMarketAccessPoints = useMemo(
    () => accessPoints.some(ap => ap.providerNetworkType === ProviderNetworkTypeEnum.Network || ap.providerNetworkType === ProviderNetworkTypeEnum.Prospect),
    [accessPoints]
  );

  const accessPointsForTargeting = useMemo(() => {
    return accessPoints.filter(ap => outOfNetworkTypes.includes(ap.providerNetworkType)
      && !([ProviderActionStatusEnum.FullyTargeted].includes(ap.providerActionStatus)));
  }, [accessPoints, outOfNetworkTypes]);

  const accessPointsForExclusion = useMemo(() => {
    return accessPoints.filter(ap => outOfNetworkTypes.includes(ap.providerNetworkType)
      && !([ProviderActionStatusEnum.FullyExcluded].includes(ap.providerActionStatus)));
  }, [accessPoints, outOfNetworkTypes]);

  const hasAvailableActions = useMemo(() => {
    const hasExcludeRole = userContext.roles.includes(Roles.PROVIDER_EXCLUDE_USER);
    const hasTargetRole = userContext.roles.includes(Roles.PROVIDER_TARGET_USER);
    const hasTerminateRole = userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN);

    if (!hasExcludeRole && !hasTargetRole && !hasTerminateRole) {
      return false;
    }

    const hasExcludeAction = hasExcludeRole && outOfNetworkAccessPoints.length > 0;
    const hasTargetAction = hasTargetRole && outOfNetworkAccessPoints.length > 0;
    const hasTerminateAction = hasTerminateRole && networkAccessPoints.length > 0;

    return hasExcludeAction || hasTargetAction || hasTerminateAction;
  }, [outOfNetworkAccessPoints, networkAccessPoints, userContext.roles]);

  const showTerminated =
    userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN) ||
    userContext.roles.includes(Roles.TERM_PROVIDER_VIEWER);

  const showLocationConfidence = providerHubContext.showAccessPointLocationConfidence;

  const filteredSortedAccessPoints = useMemo<ServicingAccessPoint[]>(() => {
    let result = accessPoints;

    if (filterState.providerSources.length > 0) {
      result = result.filter(ap => filterState.providerSources.includes(ap.providerNetworkType));
    }
    if (filterState.actionStatuses.length > 0) {
      result = result.filter(ap => filterState.actionStatuses.includes(ap.providerActionStatus));
    }
    if (filterState.locationConfidences.length > 0) {
      result = result.filter(ap => filterState.locationConfidences.includes(ap.locationConfidenceScore ?? null));
    }

    if (sortOption === AccessPointSortOptionEnum.Default) {
      return [...result].sort((a, b) => {
        const aOrder = providerNetworkTypeDisplayOrder.indexOf(a.providerNetworkType);
        const bOrder = providerNetworkTypeDisplayOrder.indexOf(b.providerNetworkType);
        return (aOrder === -1 ? 99 : aOrder) - (bOrder === -1 ? 99 : bOrder);
      });
    }
    if (sortOption === AccessPointSortOptionEnum.OutOfNetworkFirst) {
      return [...result].sort((a, b) => {
        const aOut = a.providerNetworkType !== ProviderNetworkTypeEnum.Network ? 0 : 1;
        const bOut = b.providerNetworkType !== ProviderNetworkTypeEnum.Network ? 0 : 1;
        return aOut - bOut;
      });
    }
    if (sortOption === AccessPointSortOptionEnum.TerminatedFirst) {
      return [...result].sort((a, b) => {
        const aT = a.providerActionStatus === ProviderActionStatusEnum.FullyTerminated ? 0 : 1;
        const bT = b.providerActionStatus === ProviderActionStatusEnum.FullyTerminated ? 0 : 1;
        return aT - bT;
      });
    }
    if (sortOption === AccessPointSortOptionEnum.LocationConfidenceHighToLow) {
      return [...result].sort((a, b) => {
        const aScore = a.locationConfidenceScore ? (CONFIDENCE_SORT_ORDER[a.locationConfidenceScore] ?? 0) : 0;
        const bScore = b.locationConfidenceScore ? (CONFIDENCE_SORT_ORDER[b.locationConfidenceScore] ?? 0) : 0;
        return bScore - aScore;
      });
    }

    return result;
  }, [accessPoints, filterState, sortOption]);

  const activeFilterChips = useMemo<FilterChipDescriptor[]>(() => {
    const chips: FilterChipDescriptor[] = [];

    for (const source of filterState.providerSources) {
      const label = providerNetworkTypeDisplayName(source);
      chips.push({
        ariaLabel: `Remove filter ${label}`,
        content: label,
        key: `source-${source}`,
        onDismiss: () => setFilterState(prev => ({
          ...prev,
          providerSources: prev.providerSources.filter(s => s !== source),
        })),
      });
    }

    for (const { key, label, statuses } of ACTION_CHIP_CONFIGS) {
      if (statuses.some(s => filterState.actionStatuses.includes(s))) {
        chips.push({
          ariaLabel: `Remove filter ${label}`,
          content: label,
          key,
          onDismiss: () => setFilterState(prev => ({
            ...prev,
            actionStatuses: prev.actionStatuses.filter(s => !statuses.includes(s)),
          })),
        });
      }
    }

    for (const confidence of filterState.locationConfidences) {
      const label = `Confidence: ${locationConfidenceScoreToDisplay(confidence)}`;
      chips.push({
        ariaLabel: `Remove filter ${label}`,
        content: label,
        key: `confidence-${confidence ?? "none"}`,
        onDismiss: () => setFilterState(prev => ({
          ...prev,
          locationConfidences: prev.locationConfidences.filter(c => c !== confidence),
        })),
      });
    }

    return chips;
  }, [filterState]);


  const sortOptions = useMemo<AccessPointSortOptionEnum[]>(() => {
    const hasTerminatedAps = accessPoints.some(
      ap => ap.providerActionStatus === ProviderActionStatusEnum.FullyTerminated,
    );
    const hasConfidenceAps = accessPoints.some(ap => ap.locationConfidenceScore != null);

    const options = [AccessPointSortOptionEnum.Default];
    if (outOfNetworkAccessPoints.length > 0) options.push(AccessPointSortOptionEnum.OutOfNetworkFirst);
    if (showTerminated && hasTerminatedAps) options.push(AccessPointSortOptionEnum.TerminatedFirst);
    if (showLocationConfidence && hasConfidenceAps) options.push(AccessPointSortOptionEnum.LocationConfidenceHighToLow);
    return options;
  }, [accessPoints, networkAccessPoints, outOfNetworkAccessPoints, showTerminated, showLocationConfidence]);

  const showPciData = !!projectContext?.runConfiguration?.showPciData;
  const referrer = providerHubContext.referrer;

  const marketNetworkIntegrityEnabled = featureFlagContext.enabledFeatureFlags.has(Features.NETWORK_INTEGRITY_MARKET_PROVIDER_RESULTS);

  useEffect(() => {
    // location.pathname should be /products/:productId/provider-hub[/run/:runConfigId/provider-profile/:providerKey] (where [] is optional)
    // so keeping only the first three ("", "products", productId) and updating the module should be safe regardless of the extra parameters
    const productUrl = location.pathname.split("/").slice(0, 3).join("/") + "/adequacy";
    // The crumb label reflects where the user drilled from; the click is always goBackFromProfile.
    const middleCrumb: Breadcrumb = {
      onClick: handleGoBack,
      text: REFERRER_CRUMB_TEXT[referrer] ?? "Provider Hub",
    };

    projectContext.setBreadcrumbItems([
      {
        link: productUrl,
        text: projectContext.project.resource.name,
      },
      middleCrumb,
      {
        text: !selectedProvider
          ? providerHubContext.errorLoadingProvider
            ? "Error"
            : "Loading"
          : selectedProvider?.name,
      }
    ]);
  }, [projectContext.project.resource.name, providerHubContext.selectedProvider]);

  const fetchAccessPoints = useCallback(() => {
    providerHubService
      .getAccessPointsByServicingProviders(projectContext?.project?.projectId, selectedProvider.projectRunConfigurationId, selectedProvider.servicingProviderKey)
      .then((ap) => {
        setAccessPoints(ap);
        setIsLoadingAccessPoints(false);
        if (ap?.length > 0) {
          onSelectAccessPoint(ap[0]);
        }
      });
  }, [projectContext?.project?.projectId, selectedProvider, providerHubService]);

  useEffect(() => {
    if (!!selectedProvider) {
      fetchAccessPoints();
    }
  }, [selectedProvider]);

  useEffect(() => {
    setFilterState(emptyAccessPointFilterState);
    setIsFilterModalOpen(false);
    setSortOption(AccessPointSortOptionEnum.Default);
  }, [selectedProvider]);

  useEffect(() => {
    let page = providerHubContext.accessPointsPage;
    const totalPages = Math.ceil(filteredSortedAccessPoints.length / providerHubContext.accessPointsPageSize);

    // If the current page is greater than the total pages, reset to page 1
    if (page > totalPages) {
      page = 1;
      providerHubContext.setAccessPointsPage(1);
    }

    providerHubContext.setTotalAccessPointsPages(totalPages);

    providerHubContext.setPagedAccessPoints(filteredSortedAccessPoints.slice((page - 1) * providerHubContext.accessPointsPageSize, page * providerHubContext.accessPointsPageSize));

  }, [filteredSortedAccessPoints, providerHubContext.accessPointsPage, providerHubContext.accessPointsPageSize]);

  const onSelectAccessPoint = (newAccessPoint: ServicingAccessPoint) => {
    setSelectedAccessPoint(newAccessPoint);

    if (!accessPointDetailsMap.has(newAccessPoint.servicingAccessPointId)) {
      setIsLoadingApDetails(true);
      providerHubService.getAccessPointDetails(newAccessPoint.servicingAccessPointId, projectContext?.project?.projectId, selectedProvider.projectRunConfigurationId).then((details) => {
        const newAccessPointDetailsMap = new Map(accessPointDetailsMap);
        newAccessPointDetailsMap.set(newAccessPoint.servicingAccessPointId, details);
        setAccessPointDetailsMap(newAccessPointDetailsMap);
        setIsLoadingApDetails(false);
      })
    }
  }

  useEffect(() => {
    if (!selectedProvider) {
      setAffiliations([]);
      resetAffiliationProfileAvailability();
      setIsLoadingAffiliations(false);
      return;
    }

    const abortController = new AbortController();
    setIsLoadingAffiliations(true);
    resetAffiliationProfileAvailability();
    providerAffiliationService.GetLatestContractEntitiesByNpi1(selectedProvider.npi, false)
      .then((entities) => {
        if (abortController.signal.aborted) {
          return;
        }
        setAffiliations(entities);
        loadAffiliationProfileAvailability(entities, abortController.signal);
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setAffiliations([]);
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsLoadingAffiliations(false);
        }
      });

    return () => abortController.abort();
  }, [selectedProvider]);

  const resetAffiliationProfileAvailability = () => {
    setContractEntityIdsWithoutProfile(new Set());
    setOrganizationNavContractEntityIdByNpi(new Map());
    setIsOrganizationAvailabilityResolved(false);
  };

  // Determine which contract entities / organizations shown in the accordion actually have a profile for this
  // project run configuration, so the accordion can suppress hyperlinks that would spin forever. Contract
  // entities fail open (see the state declarations); organizations cannot, because without the resolved
  // contract entity from this lookup there is nowhere for the click to go.
  const loadAffiliationProfileAvailability = (entities: ContractEntityAffiliationModel[], signal: AbortSignal) => {
    const projectId = projectContext?.project?.projectId;
    const projectRunConfigurationId = projectContext?.runConfiguration?.projectRunConfigurationId;
    // With no affiliation rows the accordion still renders a fallback row for the provider's professional
    // contract entity, so that id has to be checked too - otherwise a profile-less fallback CE stays linked.
    const fallbackContractEntityId = entities.length === 0 && selectedProvider?.professionalContractEntityName
      ? selectedProvider?.professionalAffiliationId
      : undefined;
    if (!projectId || !projectRunConfigurationId || (entities.length === 0 && fallbackContractEntityId === undefined)) {
      return;
    }

    const contractEntityIds = [...new Set([
      ...entities.map((e) => e.contractEntityId),
      ...(fallbackContractEntityId === undefined ? [] : [fallbackContractEntityId]),
    ])];
    const organizationNpis = [...new Set(entities.flatMap((e) => (e.npi2Affiliations ?? []).map((org) => org.npi2)))];

    providerHubService.getAffiliationProfileAvailability(projectId, projectRunConfigurationId, contractEntityIds, organizationNpis, signal)
      .then((availability) => {
        if (signal.aborted) {
          return;
        }
        const ceWithProfile = new Set(availability.contractEntityIdsWithProfile ?? []);
        // An organization is matched by NPI alone; the contract entity returned is the one whose profile
        // actually resolves, which we keep for navigation (it can differ from the accordion's parent CE).
        const navCeByNpi = new Map((availability.organizationsWithProfile ?? []).map((o) => [o.npi2, o.contractEntityId] as const));
        setContractEntityIdsWithoutProfile(new Set(contractEntityIds.filter((id) => !ceWithProfile.has(id))));
        setOrganizationNavContractEntityIdByNpi(navCeByNpi);
        setIsOrganizationAvailabilityResolved(true);
      })
      .catch(() => {
        if (signal.aborted) {
          return;
        }
        resetAffiliationProfileAvailability();
      });
  };

  useEffect(() => {
    if (!selectedProvider) {
      setEntityType(EntityTypeEnum.Unknown);
      setIsLoadingEntityType(false);
      return;
    }
    setIsLoadingEntityType(true);
    providerAffiliationService.GetNPPESEntityTypeByNPI(selectedProvider.npi)
      .then(setEntityType)
      .catch(() => setEntityType(EntityTypeEnum.Unknown))
      .finally(() => setIsLoadingEntityType(false));
  }, [selectedProvider]);

  // If the selected sort option is no longer available (e.g. APs reloaded), fall back to Default.
  useEffect(() => {
    if (!sortOptions.includes(sortOption)) {
      setSortOption(AccessPointSortOptionEnum.Default);
    }
  }, [sortOptions]); // sortOption is read-only here — effect reacts only to available options changing

  const handleClearFilters = useCallback(() => {
    setFilterState(emptyAccessPointFilterState);
  }, []);

  const handleCloseFilterModal = useCallback(() => setIsFilterModalOpen(false), []);

  const handleAffiliationContractEntityClick = async (contractEntityId: number) => {
    try {
      const details = await providerHubService.getContractEntityDetails(
        projectContext.project.projectId,
        projectContext.runConfiguration.projectRunConfigurationId,
        contractEntityId
      );
      providerHubContext.navigateToContractEntityProfile(details, ProviderProfileReferrerEnum.ProviderProfile);
    } catch {
      // No entity to route to on failure. Keep the user on the provider profile (URL unchanged) and surface a
      // toast, matching how load failures are handled elsewhere, rather than routing to a Contract Entity error
      // view whose URL wouldn't survive a refresh (the URL is the source of truth for which profile shows).
      toast("We could not load the selected contract entity.", {
        containerId: "z-index-9999",
        type: "error",
      });
    }
  };

  // Navigate to an organization's profile from the affiliation accordion. The organization profile (and its
  // semantic URL) is scoped to the org's OWN professional contract entity - the one under which its aggregate
  // profile resolves - which is what the availability lookup recorded and can differ from the CE the org is
  // listed beneath here. This mirrors how the CE profile's organizations table drills in.
  const handleAffiliationOrganizationClick = async (organizationNpi: string) => {
    const contractEntityId = organizationNavContractEntityIdByNpi.get(organizationNpi);
    if (contractEntityId === undefined) {
      return;
    }
    try {
      const [organizationDetails, contractEntity] = await Promise.all([
        providerHubService.getOrganizationDetails(
          projectContext.project.projectId,
          projectContext.runConfiguration.projectRunConfigurationId,
          organizationNpi,
          contractEntityId
        ),
        providerHubService.getContractEntityDetails(
          projectContext.project.projectId,
          projectContext.runConfiguration.projectRunConfigurationId,
          contractEntityId
        ),
      ]);
      providerHubContext.navigateToOrganizationProfile(organizationDetails, contractEntity, ProviderProfileReferrerEnum.ProviderProfile);
    } catch {
      toast("We could not load the selected organization.", {
        containerId: "z-index-9999",
        type: "error",
      });
    }
  };

  // Go Back is now browser Back (the URL is the source of truth for which profile shows); this wrapper just
  // clears the provider-profile error state and resets the breadcrumb before popping the history entry.
  const handleGoBack = () => {
    providerHubContext.setErrorLoadingProvider(false);
    projectContext.setBreadcrumbItems([projectContext.project.resource.name]);
    providerHubContext.goBackFromProfile();
  };

  if (!selectedProvider && !providerHubContext.errorLoadingProvider) {
    return <QesIcon iconName={QesIcons.LoadingV1} className="qes-icon-spin" description="loading provider" />;
  } else if (providerHubContext.errorLoadingProvider) {
    return <QESAlert type="danger" persist={true}>We did not find the requested provider.</QESAlert>
  }

  return (
    <>
      <div className="provider-profile__grid">
        <div className="provider-profile__top-row">
          <div className="provider-profile__top-row-buttons">
            <QESButton
              qesType="outline-inherit"
              qesTestId="provider-hub-provider-detail__back-to-results"
              onClick={handleGoBack}
              className="provider-profile__back-to-results"
            >
              Go Back
            </QESButton>
            <div className="provider-profile__quinn-container">
              {showLegacyQuinnButtons(featureFlagContext.enabledFeatureFlags, userContext.roles) &&
                <QESButton
                  className="provider-profile__quinn"
                  onClick={() => projectContext.setVirtualAssistantContext({ isOpen: true })}
                  qesType="outline"
                  qesTestId="provider-hub__show-virtual-assistant"
                >
                  <QesIcon iconName={QesIcons.Quinn} description="Quinn" />
                  <div>
                    Ask Quinn
                  </div>
                </QESButton>
              }
            </div>
            {(!providerHubContext.isScenarioModelingProcessing &&
              !providerHubContext.isScenarioModelingError &&
              featureFlagContext.enabledFeatureFlags.has(Features.PROVIDER_HUB_PROJECTION) &&
              (hasAvailableActions || isLoadingAccessPoints)) &&
              <RoleRestricted allowedRoles={[Roles.PROVIDER_EXCLUDE_USER, Roles.PROVIDER_TARGET_USER, Roles.TERM_PROVIDER_ADMIN]}>
                <div className="provider-profile__provider-actions-info">
                  {selectedProvider?.npiStatus !== NPIStatus.Missing && (
                    <>
                      <div>
                        <TooltipWrapper
                          text="Actions will affect all applicable provider access points"
                          position="bottom"
                        >
                          <QesIcon iconName={QesIcons.InfoCircle} description="Tooltip" />
                        </TooltipWrapper>
                      </div>
                      <NewActionMenu
                        alignRight={true}
                        buttonText="Provider Actions"
                        containerClassnames="provider-profile__provider-actions-menu"
                        testId="provider-actions-select"
                        buttonClassNames="qes-button-primary"
                      >
                        {outOfNetworkAccessPoints.length > 0 &&
                          <RoleRestricted allowedRoles={[Roles.PROVIDER_EXCLUDE_USER]}>
                            <FlyoutLink
                              content={{
                                body: <ProviderHubProfileActionFlyout
                                  accessPoints={effectiveAccessPointsForProjection}
                                  providerAction={ProviderProfileActionEnum.Exclude}
                                  allAccessPoints={accessPoints.length === accessPointsForProjection.length}
                                  onSubmit={fetchAccessPoints}
                                  targetedAccessPointsCount={outOfNetworkAccessPoints.length - accessPointsForTargeting.length}
                                />,
                                title: "Exclude Provider"
                              }}
                              testId="provider-hub-provider-profile__exclude-flyout"
                              classNames="action-menu-item col-12 fw-normal"
                              size="60"
                              disabled={accessPointsForExclusion.length === 0}
                            >
                              {accessPointsForExclusion.length === 0 ? (
                                <TooltipWrapper text="This provider is fully excluded across all applicable access points" position="bottom">
                                  Exclude
                                </TooltipWrapper>
                              ) : (
                                "Exclude"
                              )}
                            </FlyoutLink>
                          </RoleRestricted>
                        }

                        {outOfNetworkAccessPoints.length > 0 &&
                          <RoleRestricted allowedRoles={[Roles.PROVIDER_TARGET_USER]}>
                            <FlyoutLink
                              content={{
                                body: <ProviderHubProfileActionFlyout
                                  accessPoints={effectiveAccessPointsForProjection}
                                  providerAction={ProviderProfileActionEnum.Target}
                                  allAccessPoints={accessPoints.length === accessPointsForProjection.length}
                                  onSubmit={fetchAccessPoints}
                                  excludedAccessPointsCount={outOfNetworkAccessPoints.length - accessPointsForExclusion.length}
                                />,
                                title: "Target Provider"
                              }}
                              testId="provider-hub-provider-profile__target-flyout"
                              classNames="action-menu-item col-12 fw-normal"
                              size="40"
                              disabled={accessPointsForTargeting.length === 0}
                            >
                              {accessPointsForTargeting.length === 0 ? (
                                <TooltipWrapper text="This provider is fully targeted across all applicable access points" position="bottom">
                                  Target
                                </TooltipWrapper>
                              ) : (
                                "Target"
                              )}
                            </FlyoutLink>
                          </RoleRestricted>
                        }

                        {networkAccessPoints.length > 0 &&
                          <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
                            <FlyoutLink
                              content={{
                                body: <ProviderHubProfileActionFlyout
                                  accessPoints={accessPointsForTermination}
                                  providerAction={ProviderProfileActionEnum.Terminate}
                                  allAccessPoints={accessPoints.length === accessPointsForTermination.length}
                                  onSubmit={fetchAccessPoints}
                                />,
                                title: "Terminate Provider"
                              }}
                              testId="provider-hub-provider-profile__terminate-flyout"
                              classNames="action-menu-item col-12 fw-normal"
                              size="40"
                              disabled={accessPointsForTermination.length === 0}
                            >
                              {accessPointsForTermination.length === 0 ? (
                                <TooltipWrapper text="This provider is fully terminated across all applicable access points" position="bottom">
                                  Terminate
                                </TooltipWrapper>
                              ) : (
                                "Terminate"
                              )}
                            </FlyoutLink>
                          </RoleRestricted>
                        }
                      </NewActionMenu>
                    </>
                  )}
                </div>
              </RoleRestricted>
            }
          </div>
        </div>

        <h1 className="provider-profile__name">
          {selectedProvider.name}
          {(hasNonMarketAccessPoints || marketNetworkIntegrityEnabled) && (
            <NetworkIntegrityNotificationFlag hasNetworkIntegrityFailure={selectedProvider.hasNetworkIntegrityFailure} />
          )}
        </h1>

        {/* First Column */}
        <div className="provider-profile__grid-column--scroll">
          {!adequacyCriticalityLoading && adequacyProviderScore != null ? (
            <div className="provider-profile__cards-row">
              <ProviderProfileKeyPoints />
              <ProviderProfileCriticality />
            </div>
          ) : (<ProviderProfileKeyPoints />)}
          {!!selectedAccessPoint &&
            (<ProviderProfileTopMarketAlternatives
              selectedAccessPointId={selectedAccessPoint?.servicingAccessPointId}
            />)}
          <AccordionContainer>
            <ProviderProfileOverview />
          </AccordionContainer>
          {showPciData && !!selectedProvider && (
            <AccordionContainer>
              <ProviderHubPCIAccordion />
            </AccordionContainer>
          )}
          {featureFlagContext.enabledFeatureFlags.has(Features.ENABLE_NETWORK_INTEGRITY_IN_PROVIDER_HUB)
            && projectContext.project.showNetworkIntegrityDashboard
            && (hasNonMarketAccessPoints || marketNetworkIntegrityEnabled)
            && (
            <AccordionContainer>
              <ProviderProfileNetworkIntegrity />
            </AccordionContainer>)}
          {projectContext.project.showAffiliationsInsights && featureFlagContext.enabledFeatureFlags.has(Features.PROVIDER_AFFILIATIONS_IN_PROVIDER_HUB_UI) &&
            <>
              <ProviderProfileAffiliationAccordion
                affiliations={affiliations}
                loading={isLoadingAffiliations || isLoadingEntityType}
                entityType={entityType}
                providerAffiliationVersionId={projectContext.runConfiguration.providerAffiliationVersionId}
                professionalAffiliationId={selectedProvider.professionalAffiliationId}
                professionalContractEntityName={selectedProvider.professionalContractEntityName}
                isGhost={selectedProvider.isProviderGhost}
                onContractEntityClick={handleAffiliationContractEntityClick}
                onOrganizationClick={handleAffiliationOrganizationClick}
                contractEntityIdsWithoutProfile={contractEntityIdsWithoutProfile}
                organizationNavContractEntityIdByNpi={organizationNavContractEntityIdByNpi}
                isOrganizationAvailabilityResolved={isOrganizationAvailabilityResolved}
              />
            </>
          }
        </div>

        {/* Second Column */}
        <div className="provider-profile__grid-column">
          <div className="provider-profile__access-point-list-main-container">
            <div className="provider-profile__ap-locked-header">
              <div className="provider-profile__access-points-header">
                <h5 className="provider-profile__access-points-label">
                  Access Points
                  <span className="provider-profile__access-points-count"><span className="provider-profile__access-points-count-number">{filteredSortedAccessPoints.length}</span> of <span className="provider-profile__access-points-count-number">{accessPoints.length}</span> shown</span>
                </h5>
                <div className="provider-profile__access-points-controls">
                  <QESSelect
                    allowEmpty={false}
                    label="Sort by"
                    onChange={(e) => setSortOption(e.target.value as AccessPointSortOptionEnum)}
                    testId="ap-sort-select"
                    value={sortOption}
                  >
                    {sortOptions.map(option => (
                      <option key={option} value={option}>
                        {accessPointSortOptionLabel(option)}
                      </option>
                    ))}
                  </QESSelect>
                  <TooltipWrapper text="Filters" position="bottom">
                    <QESButton
                      aria-label="Filters"
                      aria-pressed={isFilterModalOpen}
                      className="provider-profile__ap-filter-icon-btn"
                      onClick={() => setIsFilterModalOpen(open => !open)}
                      qesRef={filtersButtonRef}
                      qesTestId="ap-filters-toggle"
                      qesType="outline"
                    >
                      <QesIcon iconName={QesIcons.FilterLines} width="22px" height="22px" isDecorative />
                      <span className="provider-profile__ap-filter-btn-label">Filters</span>
                      {activeFilterChips.length > 0 && <span className="provider-profile__filter-count-badge">{activeFilterChips.length}</span>}
                    </QESButton>
                  </TooltipWrapper>
                </div>
              </div>
              <hr className="provider-profile__ap-header-separator" />
              {!isFilterModalOpen && activeFilterChips.length > 0 && (
                <div className="provider-profile__ap-chips-bar">
                  <QesActiveFiltersBar
                    chips={activeFilterChips}
                    onClearAll={handleClearFilters}
                    testIdPrefix="ap-active-filter"
                    variant="minimal"
                  />
                </div>
              )}
            </div>
            <div className="provider-profile__ap-list-and-modal-container">
              {isFilterModalOpen && (
                <AccessPointFilterModal
                  allAccessPoints={accessPoints}
                  filterState={filterState}
                  onChange={setFilterState}
                  onClearFilters={handleClearFilters}
                  onClose={handleCloseFilterModal}
                  showLocationConfidence={showLocationConfidence}
                  showTerminated={showTerminated}
                  triggerRef={filtersButtonRef}
                />
              )}
              <div className="provider-profile__access-point-list">
                {isLoadingAccessPoints ? <QesIcon iconName={QesIcons.LoadingV1} description="loading access points" className="qes-icon-spin" /> : (
                  providerHubContext.pagedAccessPoints?.map((ap, index) => (
                    <AccessPointCard
                      accessPoint={ap}
                      key={ap.servicingAccessPointId}
                      index={index}
                      isSelected={selectedAccessPoint?.servicingAccessPointId === ap.servicingAccessPointId}
                      onSelectAccessPoint={onSelectAccessPoint}
                    />
                  ))
                )}
              </div>
              <AccessPointCardsPaging />
            </div>
          </div>
        </div>

        {/* Third Column */}
        <div className="provider-profile__grid-column">
          <AccessPointDetailsSection
            isLoadingApDetails={isLoadingApDetails}
            accessPointDetails={!!selectedAccessPoint ? accessPointDetailsMap.get(selectedAccessPoint.servicingAccessPointId) : undefined}
            selectedAccessPoint={selectedAccessPoint}
            hasNonMarketAccessPoints={hasNonMarketAccessPoints}
            onProviderActionSubmit={fetchAccessPoints}
          />
        </div>
      </div>
    </>
  )
}